/**
 * NewsDigestSignup — email signup with PDPA 4-field consent.
 *
 * Per design source §3.3 + DECISIONS S2 schema patch:
 *   - consent_recorded_at, consent_user_agent, consent_text_version,
 *     privacy_policy_version, consent_evidence_r2_key (server-side)
 *   - Default unchecked PDPA consent box (Luật PDP 2026 Điều 11)
 *   - Topic opt-in granular (default empty — must pick ≥1)
 *
 * Form posts to POST /api/news/digest/subscribe — server records all
 * 4 PDPA evidence fields. Client only collects email + topics + consent.
 */

import { useState } from 'react';
import { useLang } from '../../use-lang';
import { resolveHotNews } from '../../i18n/hotnews';
import { subscribeNewsDigest } from '../../lib/news-api';

const TOPICS = [
  'general',
  'lao_dong',
  'thue',
  'visa',
  'dat_dai',
  'hon_nhan',
  'hinh_su',
  'kinh_doanh',
  'hanh_chinh',
] as const;

type Topic = (typeof TOPICS)[number];

export function NewsDigestSignup() {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);

  const [email, setEmail] = useState<string>('');
  const [topics, setTopics] = useState<Set<Topic>>(new Set());
  const [consent, setConsent] = useState<boolean>(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'ok' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function toggleTopic(t: Topic) {
    setTopics((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    // Inline validation (server re-validates).
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setErrorMsg(copy.digest_error_invalid_email);
      setStatus('error');
      return;
    }
    if (topics.size === 0) {
      setErrorMsg(copy.digest_error_topic_required);
      setStatus('error');
      return;
    }
    if (!consent) {
      setErrorMsg(copy.digest_error_consent_required);
      setStatus('error');
      return;
    }

    setStatus('submitting');
    try {
      await subscribeNewsDigest({
        email,
        topics: [...topics],
        // Client-supplied PDPA evidence — server records consent_recorded_at +
        // consent_ip + consent_user_agent + privacy_policy_version + writes
        // consent_evidence_r2_key.
        consent_text_version: 'v1.0-2026-05-05',
        privacy_policy_version: 'v1.0-2026-05-05',
        consent_user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 256) : 'unknown',
        consent_lang: lang,
      });
      setStatus('ok');
    } catch (e) {
      setErrorMsg((e as Error).message || copy.digest_error_rate_limit);
      setStatus('error');
    }
  }

  if (status === 'ok') {
    return (
      <section style={blockStyle} aria-live="polite">
        <p style={eyebrowStyle}>{copy.digest_block_eyebrow}</p>
        <h2 style={titleStyle}>{copy.digest_success_title}</h2>
        <p style={bodyStyle}>{copy.digest_success_body.replace('{email}', email)}</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="digest-signup-heading" style={blockStyle}>
      <p style={eyebrowStyle}>{copy.digest_block_eyebrow}</p>
      <h2 id="digest-signup-heading" style={titleStyle}>{copy.digest_block_title}</h2>
      <p style={bodyStyle}>{copy.digest_block_body}</p>

      <form onSubmit={handleSubmit} noValidate style={{ marginTop: 16 }}>
        <label style={labelStyle}>
          {copy.digest_email_label}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={copy.digest_email_placeholder}
            required
            autoComplete="email"
            style={inputStyle}
          />
        </label>

        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{copy.digest_topics_label}</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {TOPICS.map((t) => {
              const labelKey = `digest_topic_${t}` as keyof typeof copy;
              const label = (copy as Record<string, string>)[labelKey] ?? t;
              const active = topics.has(t);
              return (
                <label
                  key={t}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    border: '1px solid var(--asklaw-border)',
                    borderRadius: 999,
                    background: active ? 'var(--asklaw-orange)' : 'var(--asklaw-paper)',
                    color: active ? '#fff' : 'var(--asklaw-ink-soft)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleTopic(t)}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                  />
                  {label}
                </label>
              );
            })}
          </div>
        </fieldset>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 16,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            required
            style={{ marginTop: 3, flexShrink: 0 }}
          />
          <span>
            {copy.digest_consent_checkbox}{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              {copy.digest_consent_pdpa_link}
            </a>
          </span>
        </label>

        {errorMsg ? (
          <p
            role="alert"
            style={{ marginTop: 12, color: 'var(--color-danger)', fontSize: 13 }}
          >
            {errorMsg}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="cta-primary"
          style={{ marginTop: 16 }}
        >
          {status === 'submitting' ? copy.digest_submit_loading : copy.digest_submit}
        </button>
      </form>
    </section>
  );
}

const blockStyle: React.CSSProperties = {
  background: 'var(--asklaw-paper)',
  border: '1px solid var(--asklaw-border)',
  borderRadius: 14,
  padding: 24,
  marginTop: 24,
};

const eyebrowStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  color: 'var(--asklaw-orange-deep)',
  margin: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(20px, 2.6vw, 24px)',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  margin: '6px 0 8px',
  color: 'var(--asklaw-ink)',
};

const bodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--asklaw-ink-soft)',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--asklaw-ink)',
  marginTop: 12,
};

const inputStyle: React.CSSProperties = {
  marginTop: 4,
  padding: '10px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  border: '1px solid var(--asklaw-border-strong)',
  borderRadius: 8,
  background: 'var(--asklaw-paper)',
  color: 'var(--asklaw-ink)',
};

const fieldsetStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 0,
  border: 0,
};

const legendStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--asklaw-ink)',
};
